import React from 'react';

import { EntityAttribute } from '@benzinga/content-manager';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatDate } from '@benzinga/date-utils';

import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faTwitterSquare } from '@fortawesome/free-brands-svg-icons/faTwitterSquare';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

interface EntityAttributeDisplayProps {
  attribute: EntityAttribute;
}

export const EntityAttributeDisplay: React.FC<EntityAttributeDisplayProps> = ({ attribute }) => {
  switch (attribute.type) {
    case 'button':
      return <EntityAttributeButton attribute={attribute} />;
    case 'text':
      return <EntityAttributeText attribute={attribute} />;
    case 'date':
      return <EntityAttributeDate attribute={attribute} />;
    case 'website':
      return <EntityAttributeWebsite attribute={attribute} />;
    case 'social':
      return <EntityAttributeSocial attribute={attribute} />;
    case 'address':
      return <EntityAttributeText attribute={attribute} />;
    case 'phone_number':
      return <EntityAttributePhoneNumber attribute={attribute} />;
    case 'email':
      return <EntityAttributeEmail attribute={attribute} />;
    case 'textarea':
      return <EntityAttributeTextArea attribute={attribute} />;
    case 'attributes':
      return <EntityAttributes attribute={attribute} />;
    case 'people':
      return <EntityAttributePeople attribute={attribute} />;
  }
  return null;
};

const EntityAttributes: React.FC<EntityAttributeDisplayProps> = ({ attribute }) => {
  return (
    <EntityAttributesWrapper>
      <h5 className="attrs-label">{attribute.label}</h5>
      {attribute.value.map((attr, index) => (
        <EntityAttributeDisplay attribute={attr} key={`${index}-${attr?.field}`} />
      ))}
    </EntityAttributesWrapper>
  );
};

const EntityAttributesWrapper = styled.div`
  .attrs-label {
    border-bottom: solid 1px #ddd;
    padding-bottom: 0.25rem;
    padding: 0.5rem 0.75rem;
  }
  .entity-attr.text {
    margin-bottom: 0.5rem;
    .label {
      margin-bottom: 0;
    }
  }
  .entity-attr.social {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
  }
`;

const EntityAttributeText = ({ attribute }) => (
  <EntityAttributeWrapper className={`entity-attr text`}>
    {attribute.label && <h5 className="label">{attribute.label}</h5>}
    <div className="value" dangerouslySetInnerHTML={{ __html: attribute.value ?? '–' }} />
  </EntityAttributeWrapper>
);

const EntityAttributeDate = ({ attribute }) => (
  <EntityAttributeWrapper className={`entity-attr date`}>
    {attribute.label && <h5 className="label">{attribute.label}</h5>}
    <div className="value">{formatDate(attribute.value)}</div>
  </EntityAttributeWrapper>
);

const EntityAttributeTextArea = ({ attribute }) => (
  <EntityAttributeTextAreaWrapper className={`entity-attr textarea`}>
    {attribute.label && <h5 className="label">{attribute.label}</h5>}
    <div className="value" dangerouslySetInnerHTML={{ __html: attribute.value ?? '–' }} />
  </EntityAttributeTextAreaWrapper>
);

const EntityAttributeButton = ({ attribute }) => (
  <EntityAttributeButtonWrapper
    className={`entity-attr button ${attribute.style}`}
    data-label={attribute.label}
    href={attribute.value}
  >
    {attribute.label} <Icon icon={faArrowRight} />
  </EntityAttributeButtonWrapper>
);

const EntityAttributeSocial = ({ attribute }) => {
  const socialSitesNames = Object.keys(attribute).filter(social => social !== 'type');

  const getSocialSite = (name: string) => {
    const result = socialSites[name.toLowerCase()];
    if (result && attribute[name]) {
      result.link = attribute[name]?.url ?? attribute[name];
    }
    return result;
  };

  const socials = socialSitesNames.map(name => {
    const social = getSocialSite(name);
    return social;
  });

  return (
    <EntityAttributeWrapper className={`entity-attr text social`}>
      {socials.map(social => {
        return (
          <StyledSocialIconAnchor
            className="value"
            color={social?.color}
            href={social.link}
            rel="noreferrer"
            target="_blank"
          >
            <Icon icon={social.icon} />
          </StyledSocialIconAnchor>
        );
      })}
    </EntityAttributeWrapper>
  );
};

const EntityAttributeWebsite = ({ attribute }) => (
  <EntityAttributeWrapper className={`entity-attr text website`}>
    <h5 className="label">{attribute.label}</h5>
    <a
      className="value"
      dangerouslySetInnerHTML={{
        __html: sanitizeHTML(attribute.value?.replace(/https?:\/\//, '')?.replace(/\/$/, '') ?? '–'),
      }}
      href={attribute.value}
      rel="noreferrer"
      target="_blank"
    />
  </EntityAttributeWrapper>
);

const EntityAttributePhoneNumber = ({ attribute }) => (
  <EntityAttributeWrapper className={`entity-attr text phone-number`}>
    <h5 className="label">{attribute.label}</h5>
    <div className="value">
      <a
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(attribute.value ?? '–') }}
        href={`tel:${attribute.value}`}
        rel="noreferrer"
        target="_blank"
      />
    </div>
  </EntityAttributeWrapper>
);

const EntityAttributeEmail = ({ attribute }) => (
  <EntityAttributeWrapper className={`entity-attr text email`}>
    <h5 className="label">{attribute.label}</h5>
    <div className="value">
      <a
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(attribute.value ?? '–') }}
        href={`mailto:${attribute.value}`}
        rel="noreferrer"
        target="_blank"
      />
    </div>
  </EntityAttributeWrapper>
);

const EntityAttributeTextAreaWrapper = styled.div`
  padding: 0 0.75rem;
  .label {
    border-bottom: solid 1px #ddd;
    padding-bottom: 0.25rem;
  }
`;

const EntityAttributeButtonWrapper = styled.a`
  display: flex;
  width: 100%;
  background-color: ${({ theme }) => theme.colorPalette.blue500};
  padding: 10px;
  justify-content: space-between;
  align-items: center;
  color: #ffffff;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-top: 0.5rem;
`;

const EntityAttributePeople = ({ attribute }) => (
  <EntityAttributePeopleWrapper className={`entity-attr people`}>
    <h5 className="label">{attribute.label}</h5>
    <div className="people">
      {attribute.value.map((person: any) => (
        <div className="person">
          <div className="name">{person.name}</div>
          <div className="title">{person.title}</div>
        </div>
      ))}
    </div>
  </EntityAttributePeopleWrapper>
);

const socialSites = {
  facebook: {
    color: '#3b5998',
    icon: faFacebookSquare,
    link: '',
    name: 'facebook',
  },
  instagram: {
    color: '#e1306c',
    icon: faInstagram,
    link: '',
    name: 'instagram',
  },
  linkedin: {
    color: '#0072b1',
    icon: faLinkedin,
    link: '',
    name: 'linkedin',
  },
  twitter: {
    color: '#1da1f2',
    icon: faTwitterSquare,
    link: '',
    name: 'twitter',
  },
  youtube: {
    color: 'red',
    icon: faYoutube,
    link: '',
    name: 'youtube',
  },
};

const EntityAttributeWrapper = styled.div`
  padding: 0 0.75rem;
  &.text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const EntityAttributePeopleWrapper = styled.div`
  padding: 0 0.75rem;
  .label {
    border-bottom: solid 1px #ddd;
    padding-bottom: 0.25rem;
  }
  .people {
    gap: 8px;
    display: flex;
    flex-direction: column;
    .person {
      .name {
        font-weight: bold;
        display: block;
      }
      .title {
        font-style: italic;
        font-size: 0.8rem;
      }
    }
  }
`;

const StyledSocialIconAnchor = styled.a<{ color?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: 0.25rem;
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  color: ${props => props.color};
`;
